import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { SchedulesReportLayout } from "@common/types/reports/schedules-report";
import { resolveReportPackageLedgerId } from "../ReportPackageModal/reportPackageBasis.lib";
import {
    ReportPackageReportType,
    ReportPackageRowReportType,
} from "@common/types/reports/report-package";
import type { DateOnlyDateRange } from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { VStack } from "@design-system/VStack/VStack";
import { ReportPackageBuilderRow } from "./ReportPackageBuilderRow";
import {
    ReportPackageRow,
    ReportPackageRowOptions,
} from "./reportPackageRows.lib";

interface Props {
    rows: ReportPackageRow[];
    selectedEntityIds: number[];
    ledgersByEntityId: Parameters<
        typeof resolveReportPackageLedgerId
    >[0]["ledgersByEntityId"];
    reportTypes: ReportPackageReportType[];
    canGroupByClass: boolean;
    onOptionsChange: (rowId: string, options: ReportPackageRowOptions) => void;
    isRowAccrualAvailable: (row: ReportPackageRow) => boolean;
    isEntityPerRow: boolean;
    availableEntityIds: number[];
    titleErrors: ReadonlyMap<string, string | undefined>;
    packageDateRange: DateOnlyDateRange;
    pinnedPresets: readonly string[];
    onTogglePin: (id: string) => void;
    onUpdate: (
        rowId: string,
        patch: Partial<Omit<ReportPackageRow, "id" | "title">>,
    ) => void;
    onReorder: (activeId: string, overId: string) => void;
    onReportTypeChange: (
        row: ReportPackageRow,
        reportType: ReportPackageRowReportType,
        layout?: SchedulesReportLayout,
    ) => void;
    onBasisChange: (rowId: string, basis: AccountingLedgerBasis) => void;
    onTitleChange: (rowId: string, title: string) => void;
    onRemove: (rowId: string) => void;
}

export const ReportPackageBuilderRows = ({
    rows,
    selectedEntityIds,
    ledgersByEntityId,
    reportTypes,
    canGroupByClass,
    onOptionsChange,
    isRowAccrualAvailable,
    isEntityPerRow,
    availableEntityIds,
    titleErrors,
    packageDateRange,
    pinnedPresets,
    onTogglePin,
    onUpdate,
    onReorder,
    onReportTypeChange,
    onBasisChange,
    onTitleChange,
    onRemove,
}: Props) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) {
            return;
        }
        onReorder(String(active.id), String(over.id));
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
        >
            <SortableContext
                items={rows.map((row) => row.id)}
                strategy={verticalListSortingStrategy}
            >
                <VStack gap={8} role="group" aria-label="Reports">
                    {rows.map((row) => (
                        <ReportPackageBuilderRow
                            key={row.id}
                            row={row}
                            reportTypes={reportTypes}
                            selectedEntityIds={selectedEntityIds}
                            ledgersByEntityId={ledgersByEntityId}
                            canGroupByClass={canGroupByClass}
                            onOptionsChange={(options) =>
                                onOptionsChange(row.id, options)
                            }
                            packageDateRange={packageDateRange}
                            pinnedPresets={pinnedPresets}
                            onTogglePin={onTogglePin}
                            onUpdate={(patch) => onUpdate(row.id, patch)}
                            isAccrualAvailable={isRowAccrualAvailable(row)}
                            isEntityPerRow={isEntityPerRow}
                            availableEntityIds={availableEntityIds}
                            onReportTypeChange={(reportType, layout) =>
                                onReportTypeChange(row, reportType, layout)
                            }
                            onBasisChange={(basis) =>
                                onBasisChange(row.id, basis)
                            }
                            onTitleChange={(title) =>
                                onTitleChange(row.id, title)
                            }
                            titleError={titleErrors.get(row.id)}
                            onRemove={() => onRemove(row.id)}
                        />
                    ))}
                </VStack>
            </SortableContext>
        </DndContext>
    );
};
